// Utilities to compute Gestational Age (GA) and EDD (DPP)
// Priority: enforcedGestationalAge > LMP date. Otherwise, unknown.

export type GestationalSource = 'enforced' | 'lmp' | 'unknown';

export interface PregnancyLike {
  lmpDate?: { type?: 'approximate' | 'exact' | 'unknown'; date?: string | null } | null;
  enforcedGestationalAge?: { weeks?: number; days?: number; referenceDate?: string | Date } | null;
}

export interface GestationInfo {
  source: GestationalSource;
  weeks?: number; // current GA weeks
  days?: number;  // remainder days (0-6)
  totalDays?: number; // weeks*7 + days
  lmpDate?: Date; // LMP used or inferred
  edd?: Date; // Estimated Due Date (DPP)
}

const DAY_MS = 24 * 60 * 60 * 1000;
const FULL_TERM_DAYS = 280; // 40 weeks
const MIN_ENFORCED_DAYS = 21; // Ignore enforced GA if < 3 weeks, fallback to LMP
/**
 * Teto da idade gestacional exibida: 42 semanas e 0 dias.
 *
 * Passar de 42 semanas não é gestação, é dado desatualizado — ninguém volta no
 * app para dizer que pariu. A contagem para aí em vez de anunciar "45 semanas".
 * O teto é só do que se MOSTRA: a DUM e a DPP continuam sendo as reais, porque
 * são datas de fato e a DPP passada é justamente o que revela o atraso.
 */
export const MAX_GA_DAYS = 42 * 7;

/** Reparte dias em semanas e dias, já sob o teto de 42 semanas. */
function repartirComTeto(totalDays: number): { weeks: number; days: number; totalDays: number } {
  const total = Math.min(totalDays, MAX_GA_DAYS);
  return { weeks: Math.floor(total / 7), days: total % 7, totalDays: total };
}

function toStartOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

function diffDays(a: Date, b: Date): number {
  // Returns whole-day difference a - b
  const A = toStartOfDay(a).getTime();
  const B = toStartOfDay(b).getTime();
  return Math.floor((A - B) / DAY_MS);
}

function parseMaybeDate(input?: string | null): Date | null {
  if (!input) return null;
  const d = new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

export function formatGestationalAge(weeks?: number, days?: number): string {
  if (weeks == null || days == null) return 'Desconhecida';
  const w = `${weeks} semana${weeks === 1 ? '' : 's'}`;
  const d = days > 0 ? ` e ${days} dia${days === 1 ? '' : 's'}` : '';
  return w + d;
}

/**
 * Compute current Gestational Age (GA) and DPP from pregnancy data.
 * - If enforcedGestationalAge exists and is >= 3 weeks, use it directly as current GA relative to refDate.
 *   Infer LMP as refDate - GA and DPP as LMP + 280 days.
 * - Else, if LMP exists (type !== 'unknown'), compute GA by days since LMP; DPP = LMP + 280.
 * - Else, return unknown.
 */
export function computeGestationInfo(pregnancy: PregnancyLike | null | undefined, refDate: Date = new Date()): GestationInfo {
  const now = toStartOfDay(refDate);

  // 1) Enforced GA takes precedence (only if >= 3 weeks)
  const enforced = pregnancy?.enforcedGestationalAge;
  const eWeeks = enforced?.weeks;
  const eDays = enforced?.days;

  if (typeof eWeeks === 'number' && eWeeks >= 0 && typeof eDays === 'number' && eDays >= 0) {
    const totalEnforcedDays = eWeeks * 7 + eDays;

    // Check if we have a reference date to calculate progression
    const refDateStr = enforced?.referenceDate;
    const referenceDate = parseMaybeDate(refDateStr as any);

    if (totalEnforcedDays >= MIN_ENFORCED_DAYS && referenceDate) {
      // Current Age = (Now - ReferenceDate) + EnforcedAge
      const elapsedDays = diffDays(now, toStartOfDay(referenceDate));
      const currentTotalDays = totalEnforcedDays + Math.max(0, elapsedDays); // Ensure we don't go back if ref is future (unlikely)

      // Re-calculate LMP from this current total days. A DUM e a DPP saem do
      // total SEM teto: encurtá-las faria as duas andarem para a frente um dia
      // por dia depois das 42 semanas.
      const lmp = addDays(now, -currentTotalDays);
      const edd = addDays(lmp, FULL_TERM_DAYS);

      return { source: 'enforced', ...repartirComTeto(currentTotalDays), lmpDate: lmp, edd };
    }
  }

  // 2) Fallback to LMP date if available
  const rawLmpType = pregnancy?.lmpDate?.type || 'unknown';
  const rawLmpDate = parseMaybeDate(pregnancy?.lmpDate?.date || undefined);
  if (rawLmpType !== 'unknown' && rawLmpDate) {
    const lmp = toStartOfDay(rawLmpDate);
    const total = diffDays(now, lmp);
    if (total >= 0) {
      const edd = addDays(lmp, FULL_TERM_DAYS);
      return { source: 'lmp', ...repartirComTeto(total), lmpDate: lmp, edd };
    }
  }

  // 3) Unknown
  return { source: 'unknown' };
}

export function computeEddFromGestation(weeks: number, days: number, refDate: Date = new Date()): Date {
  const now = toStartOfDay(refDate);
  const total = Math.max(0, Math.floor(weeks) * 7 + Math.max(0, Math.floor(days)));
  const lmp = addDays(now, -total);
  return addDays(lmp, FULL_TERM_DAYS);
}

export function computeGestationFromLmp(lmp: string | Date, refDate: Date = new Date()): GestationInfo {
  const lmpDate = typeof lmp === 'string' ? parseMaybeDate(lmp) : lmp;
  if (!lmpDate) return { source: 'unknown' };
  const now = toStartOfDay(refDate);
  const l = toStartOfDay(lmpDate);
  const total = diffDays(now, l);
  if (total < 0) return { source: 'unknown' };
  const edd = addDays(l, FULL_TERM_DAYS);
  return { source: 'lmp', ...repartirComTeto(total), lmpDate: l, edd };
}
